// @ts-nocheck
import React from 'react';
import { HUBSPOT_UI_ENABLED } from '@/lib/hubspotUi';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Upload, Loader2, Download, Sparkles, CheckCircle2, AlertTriangle, Info, ArrowLeft, ArrowRight, FileSpreadsheet, X, Table2, UserPlus, Users, ShieldAlert } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { api } from '@/api/api-client';
import { useDialerData } from '@/contexts/DialerDataContext';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { errorMessage, errorToastOptions } from '@/services/errorMessage';
import { useDialerUI } from '@/contexts/DialerUIContext';
import * as XLSX from 'xlsx';
import {
  TARGET_FIELDS,
  FIELD_ALIASES,
  normalizeHeader,
  detectColumnType,
  detectValueTypeHint,
  autoDetectMapping,
  redetectInlineSeparator,
  parseFile,
} from '@/services/dialer/csvMapping';
import {
  normalizePhone,
  reformaterTelephone,
  telephoneAReformater,
  applyMobileToAdditionalPhones,
} from '@/services/dialer/phoneImport';
import { downloadCsv } from '@/lib/exportCsv';

/* ── Confidence badges + legend (auto-detect transparency) ── */
const CONFIDENCE_META = {
    exact: { labelKey: 'dialer:addProspect.confidence.exact', tone: 'green', helpKey: 'dialer:addProspect.confidence.exactHelp' },
    fuzzy: { labelKey: 'dialer:addProspect.confidence.fuzzy', tone: 'amber', helpKey: 'dialer:addProspect.confidence.fuzzyHelp' },
    value: { labelKey: 'dialer:addProspect.confidence.value', tone: 'sky', helpKey: 'dialer:addProspect.confidence.valueHelp' },
};

function ConfidenceBadge({ level }) {
    const { t } = useTranslation(['dialer', 'common']);
    const meta = CONFIDENCE_META[level];
    if (!meta) return null;
    const cls = {
        green: 'bg-green-100 text-green-700 border-green-200',
        amber: 'bg-amber-100 text-amber-700 border-amber-200',
        sky: 'bg-sky-100 text-sky-700 border-sky-200',
    }[meta.tone];
    return (
        <span
            className={`inline-flex items-center text-[9px] font-semibold uppercase tracking-wider rounded border px-1.5 py-0.5 ${cls}`}
            title={t(meta.helpKey)}
        >
            {t(meta.labelKey)}
        </span>
    );
}

function ConfidenceLegend() {
    return (
        <div className="hidden sm:flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <ConfidenceBadge level="exact" />
            <ConfidenceBadge level="fuzzy" />
            <ConfidenceBadge level="value" />
        </div>
    );
}

const TYPE_HINT_META = {
    date: { labelKey: 'dialer:addProspect.typeHint.date', cls: 'bg-violet-100 text-violet-700 border-violet-200' },
    number: { labelKey: 'dialer:addProspect.typeHint.number', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
};

/* Map server-side Zod field names to catalog keys, so the shortened cause
   follows the active language. Keep the original message visible on hover
   (title attr) so power users can still inspect the raw cause. */
const FIELD_LABEL_KEYS = {
    prospect_phone: 'common:field.phone',
    prospect_name: 'common:field.name',
    prospect_email: 'common:field.email',
    prospect_company: 'common:field.company',
    prospect_title: 'common:field.title',
};

function humanizeImportReason(raw, t) {
    if (!raw) return t('dialer:addProspect.errors.unknown');
    // "field: msg": pick the first field/message pair
    const m = raw.match(/^([\w_.]+):\s*(.+?)(?:;|$)/);
    if (m) {
        const labelKey = FIELD_LABEL_KEYS[m[1]];
        const field = labelKey ? t(labelKey) : m[1];
        const msg = m[2].trim();
        if (/invalid phone format/i.test(msg)) return t('dialer:addProspect.errors.invalidField', { field });
        if (/required/i.test(msg)) return t('dialer:addProspect.errors.missingField', { field });
        if (/invalid email/i.test(msg) || /email/i.test(msg)) return t('dialer:addProspect.errors.invalidField', { field });
        if (/expected/i.test(msg)) return t('dialer:addProspect.errors.unexpectedFormat', { field });
        return t('dialer:addProspect.errors.fieldMessage', { field, message: msg });
    }
    return raw.length > 80 ? raw.slice(0, 77) + '…' : raw;
}

function TypeHintBadge({ hint }) {
    const { t } = useTranslation(['dialer']);
    const meta = TYPE_HINT_META[hint];
    if (!meta) return null;
    return (
        <span className={`inline-flex items-center text-[9px] font-semibold uppercase tracking-wider rounded border px-1.5 py-0.5 ${meta.cls}`}>
            {t(meta.labelKey)}
        </span>
    );
}

/* ── Stepper indicator ── */
function StepIndicator({ current }) {
    const { t } = useTranslation(['dialer']);
    const steps = [
        { key: 'upload', labelKey: 'dialer:addProspect.steps.file' },
        { key: 'mapping', labelKey: 'dialer:addProspect.steps.mapping' },
        { key: 'importing', labelKey: 'dialer:addProspect.steps.import' },
    ];
    const currentIdx = steps.findIndex(s => s.key === current || (current === 'done' && s.key === 'importing'));
    return (
        <div className="flex items-center justify-center gap-1 mb-3">
            {steps.map((s, i) => {
                const isDone = i < currentIdx || current === 'done';
                const isActive = i === currentIdx && current !== 'done';
                return (
                    <React.Fragment key={s.key}>
                        {i > 0 && <div className={`h-px w-8 ${isDone || isActive ? 'bg-[var(--vf-accent-hi)]' : 'bg-border'}`} />}
                        <div className="flex items-center gap-1.5">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold
                                ${isDone ? 'bg-[var(--vf-primary)] text-white' : isActive ? 'bg-[var(--vf-accent-bg)] text-[var(--vf-accent-text)] ring-2 ring-[var(--vf-accent)]' : 'bg-muted text-muted-foreground'}`}>
                                {isDone ? <CheckCircle2 className="w-3 h-3" /> : i + 1}
                            </div>
                            <span className={`text-xs ${isDone || isActive ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{t(s.labelKey)}</span>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
}

/* ── Import step sub-component ── */
function FileImportFlow({ clientEmail, onDone, existingSources }) {
    // ADR-024 phase 2.b — auto-bind imported prospects to the Customer
    // currently selected in the sidebar. No extra UI field; the sidebar
    // selection IS the import target. If no Customer is selected (rare
    // — DialerLayout normally auto-selects one), block the import with
    // a toast so we never end up with orphan rows.
    const { t } = useTranslation(['dialer', 'common']);
    const { selectedCustomerId } = useDialerData();
    const [step, setStep] = useState('upload'); // 'upload' | 'mapping' | 'importing' | 'done'
    // #46 -- corriger les numeros mal formates a l'import. Actif par defaut :
    // un numero non E.164 est refuse par canCall() et par Twilio, donc le
    // laisser tel quel produit un prospect injoignable en silence.
    const [reformaterNumeros, setReformaterNumeros] = useState(true);
    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [headers, setHeaders] = useState([]);
    const [rows, setRows] = useState([]);
    const [sheetNames, setSheetNames] = useState([]);
    const [selectedSheet, setSelectedSheet] = useState('');
    const [mapping, setMapping] = useState({}); // { targetField: sourceHeader }
    const [confidence, setConfidence] = useState({}); // { targetField: 'exact' | 'fuzzy' | 'value' }
    const [nameSplit, setNameSplit] = useState(null); // { firstHeader, lastHeader } | null
    const [mergeName, setMergeName] = useState(false); // user opted in to concat first + last
    const [customFields, setCustomFields] = useState({}); // { sourceHeader: customKey } — extra columns to import into custom_fields
    const [dragOver, setDragOver] = useState(false);
    // List selection: 'new' or existing source name
    const [listMode, setListMode] = useState('new'); // 'new' | 'existing'
    const [newListName, setNewListName] = useState('');
    const [selectedList, setSelectedListImport] = useState('');

    // Import progress
    const [importProgress, setImportProgress] = useState({ total: 0, imported: 0, skipped: 0, errors: 0, blacklisted: 0 });
    const [importDone, setImportDone] = useState(false);
    // Per-row errors collected across batches: [{ rowNumber: 1-based file row, reason, sample }]
    const [importErrors, setImportErrors] = useState([]);
    const [showAllErrors, setShowAllErrors] = useState(false);
    const [blacklistedSamples, setBlacklistedSamples] = useState([]); // [{ rowNumber, sample, phone }] — first 50

    // Parse a specific sheet from an already-loaded workbook
    const parseSheet = useCallback((workbook, sheetName) => {
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        if (jsonData.length < 2) return { headers: [], rows: [] };
        const h = jsonData[0].map(c => String(c).trim());
        const r = jsonData.slice(1).map(row => row.map(c => String(c ?? '').trim()));
        return redetectInlineSeparator(h, r);
    }, []);

    const handleFile = useCallback(async (f) => {
        if (!f) return;
        const ext = f.name.split('.').pop().toLowerCase();
        if (!['csv', 'tsv', 'txt', 'xlsx', 'xls'].includes(ext)) {
            toast.error(t('dialer:addProspect.toasts.unsupportedFormat'));
            return;
        }
        setFile(f);
        setParsing(true);
        try {
            // For Excel, check if multiple sheets
            if (ext === 'xlsx' || ext === 'xls') {
                const buffer = await f.arrayBuffer();
                const workbook = XLSX.read(buffer, { type: 'array' });
                // Store workbook ref for sheet switching
                f._workbook = workbook;
                setSheetNames(workbook.SheetNames);
                setSelectedSheet(workbook.SheetNames[0]);
                const { headers: h, rows: r } = parseSheet(workbook, workbook.SheetNames[0]);
                if (h.length === 0 || r.length === 0) {
                    toast.error(t('dialer:addProspect.toasts.emptySheet'));
                    setFile(null);
                    setParsing(false);
                    return;
                }
                setHeaders(h);
                setRows(r);
                {
                    const detect = autoDetectMapping(h, r);
                    setMapping(detect.mapping);
                    setConfidence(detect.confidence);
                    setNameSplit(detect.nameSplit);
                    setMergeName(!!detect.nameSplit);
                }
                setCustomFields({});
            } else {
                const { headers: h, rows: r } = await parseFile(f);
                if (h.length === 0 || r.length === 0) {
                    toast.error(t('dialer:addProspect.toasts.emptyFile'));
                    setFile(null);
                    setParsing(false);
                    return;
                }
                setHeaders(h);
                setRows(r);
                setSheetNames([]);
                {
                    const detect = autoDetectMapping(h, r);
                    setMapping(detect.mapping);
                    setConfidence(detect.confidence);
                    setNameSplit(detect.nameSplit);
                    setMergeName(!!detect.nameSplit);
                }
                setCustomFields({});
            }
            setNewListName(f.name.replace(/\.(csv|tsv|txt|xlsx|xls)$/i, ''));
            setStep('mapping');
        } catch (err) {
            toast.error(t('dialer:addProspect.toasts.readError', { detail: err.message }));
            setFile(null);
        } finally {
            setParsing(false);
        }
    }, [parseSheet, t]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files[0];
        if (f) handleFile(f);
    }, [handleFile]);

    const updateMapping = (targetField, sourceHeader) => {
        setMapping(prev => {
            const next = { ...prev };
            if (sourceHeader === '__none__') {
                delete next[targetField];
            } else {
                next[targetField] = sourceHeader;
            }
            return next;
        });
        // Manual override → drop the auto-detect badge for this field
        setConfidence(prev => {
            const next = { ...prev };
            delete next[targetField];
            return next;
        });
    };

    const mappedCount = Object.keys(mapping).length;
    const hasRequiredFields = TARGET_FIELDS.filter(f => f.required).every(f => mapping[f.key]);

    const startImport = async () => {
        // ADR-024 — guard: imports MUST be attached to a Customer. The
        // sidebar selection drives this; if for any reason it's empty
        // (e.g. the user lands here right after creating their first
        // Customer), refuse rather than create orphan prospects.
        if (!selectedCustomerId) {
            toast.error(t('dialer:addProspect.toasts.selectClientFirst'));
            return;
        }
        setStep('importing');
        const total = rows.length;
        setImportProgress({ total, imported: 0, skipped: 0, errors: 0, blacklisted: 0 });
        setImportErrors([]);
        setBlacklistedSamples([]);
        setShowAllErrors(false);

        const sourceName = listMode === 'existing' && selectedList
            ? selectedList
            : (newListName.trim() || file.name.replace(/\.(csv|tsv|txt|xlsx|xls)$/i, ''));

        // Build mapped rows + parallel meta array (rowNumber for messages, sample for context)
        const prospects = [];
        const meta = []; // [{ rowNumber, sample }] — same length as prospects
        let skipped = 0;
        const customEntries = Object.entries(customFields);
        const firstIdx = mergeName && nameSplit ? headers.indexOf(nameSplit.firstHeader) : -1;
        const lastIdx = mergeName && nameSplit ? headers.indexOf(nameSplit.lastHeader) : -1;
        for (let rIdx = 0; rIdx < rows.length; rIdx++) {
            const row = rows[rIdx];
            // customer_id pre-set so the new prospects show up immediately
            // under the selected Customer in the sidebar after import.
            const prospect = { customer_id: selectedCustomerId, source: sourceName, status: 'active', priority: 0 };
            for (const [targetField, sourceHeader] of Object.entries(mapping)) {
                const colIdx = headers.indexOf(sourceHeader);
                if (colIdx !== -1) {
                    prospect[targetField] = row[colIdx] || '';
                }
            }
            if (mergeName && firstIdx !== -1 && lastIdx !== -1) {
                const first = String(row[firstIdx] ?? '').trim();
                const last = String(row[lastIdx] ?? '').trim();
                const full = `${first} ${last}`.trim();
                if (full) prospect.prospect_name = full;
            }
            // #46 -- reecriture du numero, seulement si l'agent l'a laissee active
            // ET seulement quand on sait quoi ecrire.
            if (reformaterNumeros && prospect.prospect_phone) {
                const corrige = reformaterTelephone(prospect.prospect_phone);
                if (corrige) prospect.prospect_phone = corrige;
            }
            applyMobileToAdditionalPhones(prospect, reformaterNumeros);
            if (!prospect.prospect_name && !prospect.prospect_phone) {
                skipped++;
                continue;
            }
            if (customEntries.length > 0) {
                const cf = {};
                for (const [sourceHeader, customKey] of customEntries) {
                    const colIdx = headers.indexOf(sourceHeader);
                    const val = colIdx !== -1 ? String(row[colIdx] ?? '').trim() : '';
                    if (val) cf[customKey] = val;
                }
                if (Object.keys(cf).length > 0) prospect.custom_fields = cf;
            }
            prospects.push(prospect);
            meta.push({
                rowNumber: rIdx + 2,
                sample: prospect.prospect_name || prospect.prospect_phone || t('dialer:addProspect.errors.rowFallback', { row: rIdx + 2 }),
            });
        }

        if (prospects.length === 0) {
            setImportProgress({ total, imported: 0, skipped, errors: 0, blacklisted: 0 });
            setImportDone(true);
            setStep('done');
            return;
        }

        // ── Blacklist pre-check ───────────────────────────────────────────────
        // Ask the server which phones are already on the client's DNC list, then
        // tag those prospects so they land with do_not_call=true and won't be
        // dialed. No silent loss — the user sees a count + sample on the done
        // screen and can manage them via Settings → Blacklist.
        const blacklistedSet = new Set();
        try {
            const phonesToCheck = prospects.map((p) => p.prospect_phone).filter(Boolean);
            if (phonesToCheck.length > 0) {
                const res = await api.entities.ProspectQueue.checkBlacklist({ phones: phonesToCheck });
                const list = Array.isArray(res?.blacklisted) ? res.blacklisted : [];
                for (const p of list) blacklistedSet.add(normalizePhone(p));
            }
        } catch (err) {
            console.warn('[Import] Blacklist check failed, proceeding without flagging:', err);
        }

        let blacklistedCount = 0;
        const blacklistedSamplesAcc = [];
        if (blacklistedSet.size > 0) {
            for (let i = 0; i < prospects.length; i++) {
                const norm = normalizePhone(prospects[i].prospect_phone);
                if (norm && blacklistedSet.has(norm)) {
                    prospects[i].do_not_call = true;
                    prospects[i].status = 'do_not_call';
                    blacklistedCount++;
                    if (blacklistedSamplesAcc.length < 50) {
                        blacklistedSamplesAcc.push({
                            rowNumber: meta[i].rowNumber,
                            sample: meta[i].sample,
                            phone: prospects[i].prospect_phone,
                        });
                    }
                }
            }
            setBlacklistedSamples(blacklistedSamplesAcc);
        }

        const BATCH_SIZE = 50;
        let imported = 0;
        let errors = 0;
        const collectedErrors = [];
        const totalBatches = Math.ceil(prospects.length / BATCH_SIZE);
        for (let batchIdx = 0; batchIdx < totalBatches; batchIdx++) {
            const start = batchIdx * BATCH_SIZE;
            const batch = prospects.slice(start, start + BATCH_SIZE);
            const batchMeta = meta.slice(start, start + BATCH_SIZE);
            try {
                const resp = await api.entities.ProspectQueue.bulkCreate(batch);
                const createdLen = Array.isArray(resp)
                    ? resp.length
                    : Array.isArray(resp?.created) ? resp.created.length : batch.length;
                const batchErrors = Array.isArray(resp?.errors) ? resp.errors : [];
                imported += createdLen;
                errors += batchErrors.length;
                for (const e of batchErrors) {
                    const m = batchMeta[e.index];
                    if (m) collectedErrors.push({ rowNumber: m.rowNumber, sample: m.sample, reason: e.reason });
                }
            } catch (err) {
                console.error('[Import] Batch error:', err);
                errors += batch.length;
                const reason = errorMessage(err, t, 'dialer:addProspect.errors.network');
                for (const m of batchMeta) {
                    collectedErrors.push({ rowNumber: m.rowNumber, sample: m.sample, reason });
                }
            }
            await new Promise(r => setTimeout(r, 50));
            setImportProgress({ total: prospects.length + skipped, imported, skipped, errors, blacklisted: blacklistedCount });
            setImportErrors(collectedErrors.slice(0, 200));
        }

        setImportProgress({ total: prospects.length + skipped, imported, skipped, errors, blacklisted: blacklistedCount });
        setImportErrors(collectedErrors.slice(0, 200));
        setStep('done');
    };

    // Handle sheet change for multi-sheet Excel (must be before any early return)
    const handleSheetChange = useCallback((sheetName) => {
        if (!file?._workbook) return;
        setSelectedSheet(sheetName);
        const { headers: h, rows: r } = parseSheet(file._workbook, sheetName);
        setHeaders(h);
        setRows(r);
        const detect = autoDetectMapping(h, r);
        setMapping(detect.mapping);
        setConfidence(detect.confidence);
        setNameSplit(detect.nameSplit);
        setMergeName(!!detect.nameSplit);
        setCustomFields({});
    }, [file, parseSheet]);

    const reset = () => {
        setStep('upload');
        setFile(null);
        setHeaders([]);
        setRows([]);
        setMapping({});
        setConfidence({});
        setNameSplit(null);
        setMergeName(false);
        setCustomFields({});
        setImportProgress({ total: 0, imported: 0, skipped: 0, errors: 0, blacklisted: 0 });
        setImportDone(false);
        setImportErrors([]);
        setShowAllErrors(false);
        setBlacklistedSamples([]);
    };

    /* Normalize header into a stable custom_fields key: lowercase snake_case */
    const toCustomKey = (header) =>
        String(header)
            .toLowerCase()
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');

    const toggleCustomField = (sourceHeader) => {
        setCustomFields((prev) => {
            const next = { ...prev };
            if (next[sourceHeader]) {
                delete next[sourceHeader];
            } else {
                next[sourceHeader] = toCustomKey(sourceHeader);
            }
            return next;
        });
    };

    const renameCustomField = (sourceHeader, newKey) => {
        setCustomFields((prev) => {
            if (!prev[sourceHeader]) return prev;
            const sanitized = toCustomKey(newKey) || toCustomKey(sourceHeader);
            return { ...prev, [sourceHeader]: sanitized };
        });
    };

    /* Headers not bound to any TARGET_FIELD — candidates for custom_fields.
       When the firstName + lastName merge is active, the two source columns
       are consumed by the merge and must not also surface as ignorable
       custom fields (they would let the user re-import the same data twice). */
    const mergeConsumedHeaders = mergeName && nameSplit
        ? new Set([nameSplit.firstHeader, nameSplit.lastHeader])
        : new Set();
    const unmappedHeaders = headers.filter(
        (h) => !Object.values(mapping).includes(h) && !mergeConsumedHeaders.has(h),
    );

    // ── Step: Upload ──
    if (step === 'upload') {
        return (
            <div className="space-y-4">
                <StepIndicator current="upload" />
                {parsing ? (
                    <div className="border-2 border-dashed border-[var(--vf-accent-bd)] rounded-lg p-8 text-center bg-[var(--vf-accent-bg)]">
                        <Loader2 className="w-12 h-12 text-[var(--vf-accent-hi)] mx-auto mb-4 animate-spin" />
                        <p className="text-sm text-foreground/80">{t('dialer:addProspect.upload.parsing')}</p>
                    </div>
                ) : (
                    <label
                        htmlFor="prospect-file-input"
                        className={`group block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
                            dragOver
                                ? 'border-[var(--vf-accent)] bg-[var(--vf-accent-bg)] scale-[1.01] shadow-lg'
                                : 'border-[var(--vf-accent-bd)] bg-[var(--vf-accent-bg)]/60 hover:bg-[var(--vf-accent-bg)] hover:border-[var(--vf-accent)]'
                        }`}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={handleDrop}
                    >
                        <FileSpreadsheet
                            className="w-12 h-12 text-[var(--vf-accent-hi)] mx-auto mb-4 transition-transform duration-200 group-hover:scale-110 group-hover:-translate-y-0.5"
                        />
                        <p className="text-sm text-foreground/80 mb-1">
                            {t('dialer:addProspect.upload.dropzone')}
                        </p>
                        <p className="text-xs text-muted-foreground mb-5">
                            {t('dialer:addProspect.upload.formats')}
                        </p>
                        {/* Hidden native input — label wraps the whole dropzone so clicking
                            anywhere opens the picker, styled button is purely visual */}
                        <input
                            id="prospect-file-input"
                            type="file"
                            accept=".csv,.tsv,.txt,.xlsx,.xls"
                            onChange={(e) => handleFile(e.target.files[0])}
                            className="sr-only"
                        />
                        <span
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm group-hover:shadow-md active:scale-95"
                            style={{
                                background: 'var(--vf-primary)',
                                color: 'var(--vf-primary-fg)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--vf-primary-hover)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'var(--vf-primary)';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <Upload className="w-4 h-4" />
                            {t('dialer:addProspect.upload.choose')}
                        </span>
                    </label>
                )}

                <button
                    type="button"
                    onClick={() => {
                        const header = 'nom,telephone,email,entreprise,poste,linkedin,website,notes';
                        const example = 'Jean Dupont,+33612345678,jean@example.com,Acme Corp,CEO,https://linkedin.com/in/jeandupont,https://acme.com,Premier contact';
                        downloadCsv('template_prospects.csv', `${header}\n${example}\n`);
                    }}
                    className="inline-flex items-center gap-1.5 text-xs text-[var(--vf-accent)] hover:text-[var(--vf-accent-text)] font-medium hover:underline mx-auto w-full justify-center"
                >
                    <Download className="w-3.5 h-3.5" />
                    {t('dialer:addProspect.upload.template')}
                </button>
            </div>
        );
    }

    // ── Step: Mapping ──
    if (step === 'mapping') {
        const previewRows = rows.slice(0, 3);
        return (
            <div className="space-y-4">
                <StepIndicator current="mapping" />

                {/* File info bar */}
                <div className="flex items-center justify-between bg-[var(--vf-accent-bg)] border border-[var(--vf-accent-bd)] rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 text-sm">
                        <FileSpreadsheet className="w-4 h-4 text-[var(--vf-accent)]" />
                        <span className="font-medium text-[var(--vf-accent-text)]">{file.name}</span>
                        <span className="text-muted-foreground">· {t('dialer:addProspect.mapping.fileSummary', { rows: rows.length, columns: headers.length })}</span>
                    </div>
                    <button onClick={reset} className="text-muted-foreground hover:text-foreground">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Sheet selector for multi-sheet Excel */}
                {sheetNames.length > 1 && (
                    <div className="flex items-center gap-2">
                        <Label className="text-sm whitespace-nowrap">{t('dialer:addProspect.mapping.sheet')}</Label>
                        <select
                            value={selectedSheet}
                            onChange={(e) => handleSheetChange(e.target.value)}
                            className="flex-1 border border-border rounded px-2 py-1 text-sm bg-background"
                        >
                            {sheetNames.map(name => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Smart suggestion: merge first + last name into prospect_name */}
                {nameSplit && (
                    <div className="flex items-start gap-2 bg-[var(--vf-accent-bg)]/60 border border-[var(--vf-accent-bd)] rounded-lg px-3 py-2.5">
                        <Sparkles className="w-4 h-4 text-[var(--vf-accent)] mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-[var(--vf-accent-text)] mb-0.5">
                                {t('dialer:addProspect.mapping.mergeSuggestion')} <strong>{nameSplit.firstHeader}</strong> + <strong>{nameSplit.lastHeader}</strong> {t('dialer:addProspect.mapping.mergeInto')}
                            </p>
                            <p className="text-[11px] text-muted-foreground">
                                {t('dialer:addProspect.mapping.mergeHint')}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setMergeName((v) => !v)}
                            className={`shrink-0 px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                                mergeName
                                    ? 'bg-[var(--vf-accent)] text-white border-[var(--vf-accent)]'
                                    : 'bg-white text-[var(--vf-accent)] border-[var(--vf-accent-bd)] hover:bg-[var(--vf-accent-bg)]'
                            }`}
                        >
                            {mergeName ? t('dialer:addProspect.mapping.mergeOn') : t('dialer:addProspect.mapping.mergeOff')}
                        </button>
                    </div>
                )}

                {/* Mapping table */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-semibold">{t('dialer:addProspect.mapping.title')}</Label>
                        <div className="flex items-center gap-2">
                            <ConfidenceLegend />
                            <span className={`text-xs px-2 py-0.5 rounded-full ${mappedCount === TARGET_FIELDS.length ? 'bg-green-100 text-green-700' : 'bg-[var(--vf-accent-bg)] text-[var(--vf-accent-text)]'}`}>
                                {t('dialer:addProspect.mapping.mappedCount', { mapped: mappedCount, total: TARGET_FIELDS.length })}
                            </span>
                        </div>
                    </div>
                    <div className="border rounded-lg divide-y">
                        {/* Header row */}
                        <div className="grid grid-cols-[minmax(140px,200px)_minmax(180px,1fr)_minmax(120px,2fr)] bg-muted/50 text-xs font-medium text-muted-foreground">
                            <div className="px-3 py-2">{t('dialer:addProspect.mapping.targetField')}</div>
                            <div className="px-3 py-2">{t('dialer:addProspect.mapping.sourceColumn')}</div>
                            <div className="px-3 py-2">{t('dialer:addProspect.mapping.preview')}</div>
                        </div>
                        {TARGET_FIELDS.map((field) => {
                            const sourceHeader = mapping[field.key];
                            const colIdx = sourceHeader ? headers.indexOf(sourceHeader) : -1;
                            const conf = confidence[field.key];
                            // Merge name overrides the prospect_name pick visually
                            const isOverriddenByMerge = field.key === 'prospect_name' && mergeName && nameSplit;
                            return (
                                <div
                                    key={field.key}
                                    className="grid grid-cols-[minmax(140px,200px)_minmax(180px,1fr)_minmax(120px,2fr)] items-center text-sm"
                                >
                                    <div className="px-3 py-2 text-xs flex items-center gap-1.5 min-w-0">
                                        <span className="font-medium truncate">{t(field.labelKey)}</span>
                                        {field.required && <span className="text-red-500">*</span>}
                                        {sourceHeader && conf && !isOverriddenByMerge && <ConfidenceBadge level={conf} />}
                                    </div>
                                    <div className="px-3 py-2 min-w-0">
                                        {isOverriddenByMerge ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] text-[var(--vf-accent-text)] bg-[var(--vf-accent-bg)] border border-[var(--vf-accent-bd)] rounded px-2 py-1 truncate">
                                                <Sparkles className="w-3 h-3" />
                                                {t('dialer:addProspect.mapping.mergeChip', { first: nameSplit.firstHeader, last: nameSplit.lastHeader })}
                                            </span>
                                        ) : (
                                            <select
                                                value={sourceHeader || '__none__'}
                                                onChange={(e) => updateMapping(field.key, e.target.value)}
                                                className="w-full border border-border rounded px-2 py-1 text-xs bg-background truncate"
                                            >
                                                <option value="__none__">{t('dialer:addProspect.mapping.ignoreOption')}</option>
                                                {headers.map((h) => (
                                                    <option key={h} value={h}>{h}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <div className="px-3 py-2 text-muted-foreground text-[11px] truncate min-w-0">
                                        {isOverriddenByMerge
                                            ? previewRows.map((r) => `${r[headers.indexOf(nameSplit.firstHeader)] ?? ''} ${r[headers.indexOf(nameSplit.lastHeader)] ?? ''}`.trim()).filter(Boolean).slice(0, 2).join(' | ') || '-'
                                            : colIdx >= 0
                                                ? previewRows.map((r) => r[colIdx]).filter(Boolean).slice(0, 2).join(' | ') || '-'
                                                : '-'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* #46 -- numeros mal formates : combien, un apercu avant/apres,
                    et le choix de corriger ou non. */}
                {(() => {
                    const colTel = mapping['prospect_phone'] ? headers.indexOf(mapping['prospect_phone']) : -1;
                    if (colTel < 0) return null;
                    const aCorriger = rows
                        .map((r) => String(r[colTel] ?? '').trim())
                        .filter((v) => v && telephoneAReformater(v));
                    if (aCorriger.length === 0) return null;
                    const apercu = aCorriger.slice(0, 3);
                    return (
                        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3" data-testid="bandeau-reformatage">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-amber-900">
                                        {aCorriger.length > 1
                                            ? t('dialer:addProspect.phones.toFixPlural', { n: aCorriger.length })
                                            : t('dialer:addProspect.phones.toFix', { n: aCorriger.length })}
                                    </p>
                                    <p className="text-xs text-amber-800 mt-0.5">
                                        {t('dialer:addProspect.phones.expected')}
                                    </p>
                                    <div className="mt-2 space-y-0.5">
                                        {apercu.map((v, i) => (
                                            <div key={i} className="text-[11px] font-mono text-amber-900 flex items-center gap-1.5">
                                                <span className="line-through opacity-70">{v}</span>
                                                <span aria-hidden="true">→</span>
                                                <span className="font-semibold">{reformaterTelephone(v)}</span>
                                            </div>
                                        ))}
                                        {aCorriger.length > apercu.length && (
                                            <div className="text-[11px] text-amber-800">
                                                {aCorriger.length - apercu.length > 1
                                                    ? t('dialer:addProspect.phones.andOtherPlural', { n: aCorriger.length - apercu.length })
                                                    : t('dialer:addProspect.phones.andOther', { n: aCorriger.length - apercu.length })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <label className="flex items-center gap-2 text-xs text-amber-900 shrink-0 cursor-pointer">
                                    <Switch
                                        checked={reformaterNumeros}
                                        onCheckedChange={setReformaterNumeros}
                                        aria-label={t('dialer:addProspect.phones.fixAria')}
                                    />
                                    {t('dialer:addProspect.phones.fix')}
                                </label>
                            </div>
                        </div>
                    );
                })()}

                {/* Colonnes additionnelles — unmapped headers that can be imported as custom_fields */}
                {unmappedHeaders.length > 0 && (
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label className="text-sm font-semibold">{t('dialer:addProspect.custom.title')}</Label>
                            <span className="text-xs text-muted-foreground">
                                <span className="text-green-700 font-medium">{t('dialer:addProspect.custom.importedCount', { n: Object.keys(customFields).length })}</span>
                                {' · '}
                                <span>{t('dialer:addProspect.custom.ignoredCount', { n: unmappedHeaders.length - Object.keys(customFields).length })}</span>
                            </span>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                            {t('dialer:addProspect.custom.hint')}
                        </p>
                        <div className="border rounded-lg divide-y max-h-56 overflow-y-auto">
                            {unmappedHeaders.map((h) => {
                                const colIdx = headers.indexOf(h);
                                const colSamples = rows.slice(0, 10).map((r) => String(r[colIdx] ?? '').trim());
                                const samples = colSamples.filter(Boolean).slice(0, 2).join(' · ') || '-';
                                const typeHint = detectValueTypeHint(colSamples);
                                const enabled = !!customFields[h];
                                return (
                                    <div
                                        key={h}
                                        className={`grid grid-cols-[auto_minmax(140px,1.2fr)_minmax(140px,1.5fr)_minmax(120px,1fr)] items-center gap-2 px-3 py-2 transition-colors ${
                                            enabled ? 'bg-green-50/40' : 'bg-muted/20 opacity-70'
                                        }`}
                                    >
                                        {/* Switch */}
                                        <Switch
                                            checked={enabled}
                                            onCheckedChange={() => toggleCustomField(h)}
                                            aria-label={enabled
                                                ? t('dialer:addProspect.custom.disableAria', { column: h })
                                                : t('dialer:addProspect.custom.enableAria', { column: h })}
                                        />

                                        {/* Source column name + type hint */}
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            <span className={`text-xs font-medium truncate ${enabled ? 'text-foreground' : 'text-muted-foreground'}`}>
                                                {h}
                                            </span>
                                            {typeHint !== 'text' && <TypeHintBadge hint={typeHint} />}
                                        </div>

                                        {/* Inline editable target key (only when enabled) */}
                                        <div className="min-w-0">
                                            {enabled ? (
                                                <div className="flex items-center gap-1 text-[11px]">
                                                    <span className="text-muted-foreground shrink-0">→</span>
                                                    <input
                                                        type="text"
                                                        value={customFields[h]}
                                                        onChange={(e) => renameCustomField(h, e.target.value)}
                                                        className="flex-1 min-w-0 px-1.5 py-0.5 rounded border border-border bg-background font-mono text-[11px] focus:border-[var(--vf-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--vf-accent-bd)]"
                                                        spellCheck={false}
                                                    />
                                                </div>
                                            ) : (
                                                <span className="text-[11px] text-muted-foreground italic">{t('dialer:addProspect.custom.ignored')}</span>
                                            )}
                                        </div>

                                        {/* Sample values */}
                                        <div className="text-muted-foreground text-[11px] truncate min-w-0" title={samples}>
                                            {samples}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Preview table */}
                <div>
                    <Label className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                        <Table2 className="w-4 h-4" />
                        {t('dialer:addProspect.preview.title', { n: Math.min(3, rows.length) })}
                    </Label>
                    <div className="border rounded-lg overflow-auto max-h-32">
                        <table className="text-xs w-max min-w-full">
                            <thead>
                                <tr className="bg-muted/50">
                                    {headers.map((h, i) => (
                                        <th key={i} className="text-left px-2 py-1.5 font-medium text-muted-foreground whitespace-nowrap max-w-[120px] truncate">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {previewRows.map((row, ri) => (
                                    <tr key={ri} className="border-t">
                                        {headers.map((_, ci) => (
                                            <td key={ci} className="px-2 py-1 max-w-[120px] truncate">{row[ci] || ''}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Validation + actions */}
                {!hasRequiredFields && (
                    <div className="flex items-center gap-2 text-amber-600 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        <span>{t('dialer:addProspect.mapping.requiredPrefix')} <strong>{t('common:field.name')}</strong> {t('dialer:addProspect.mapping.requiredAnd')} <strong>{t('common:field.phone')}</strong> {t('dialer:addProspect.mapping.requiredSuffix')}</span>
                    </div>
                )}

                {/* List destination */}
                <div>
                    <Label className="text-sm font-semibold mb-2 block">{t('dialer:addProspect.destination.title')}</Label>
                    <div className="flex gap-2 mb-2">
                        <button
                            onClick={() => setListMode('new')}
                            className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                                listMode === 'new' ? 'bg-[var(--vf-accent-bg)] text-[var(--vf-accent-text)] border-[var(--vf-accent-bd)]' : 'bg-muted text-muted-foreground border-border hover:bg-accent'
                            }`}
                        >
                            {t('dialer:addProspect.destination.newList')}
                        </button>
                        <button
                            onClick={() => setListMode('existing')}
                            className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                                listMode === 'existing' ? 'bg-[var(--vf-accent-bg)] text-[var(--vf-accent-text)] border-[var(--vf-accent-bd)]' : 'bg-muted text-muted-foreground border-border hover:bg-accent'
                            }`}
                        >
                            {t('dialer:addProspect.destination.existingList')}
                        </button>
                    </div>
                    {listMode === 'new' ? (
                        <Input
                            value={newListName}
                            onChange={(e) => setNewListName(e.target.value)}
                            placeholder={t('dialer:addProspect.destination.newListPlaceholder')}
                            className="h-8 text-sm"
                        />
                    ) : (
                        <select
                            value={selectedList}
                            onChange={(e) => setSelectedListImport(e.target.value)}
                            className="w-full border border-border rounded px-2 py-1.5 text-sm bg-background"
                        >
                            <option value="">{t('dialer:addProspect.destination.chooseOption')}</option>
                            {(existingSources || []).map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    )}
                </div>

                <div className="flex gap-2">
                    <Button variant="outline" onClick={reset} className="flex-1">
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        {t('common:actions.back')}
                    </Button>
                    <Button
                        onClick={startImport}
                        disabled={!hasRequiredFields || (listMode === 'existing' && !selectedList)}
                        className="flex-1 bg-primary hover:bg-primary/90 text-white"
                    >
                        {t('dialer:addProspect.actions.import', { n: rows.length })}
                        <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                </div>
            </div>
        );
    }

    // ── Step: Importing ──
    if (step === 'importing') {
        const pct = importProgress.total > 0 ? Math.round(((importProgress.imported + importProgress.skipped + importProgress.errors) / importProgress.total) * 100) : 0;
        return (
            <div className="space-y-4 py-4">
                <StepIndicator current="importing" />
                <div className="text-center">
                    <Loader2 className="w-10 h-10 text-[var(--vf-accent)] animate-spin mx-auto mb-3" />
                    <p className="font-semibold text-foreground">{t('dialer:addProspect.progress.title')}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t('dialer:addProspect.progress.processed', { done: importProgress.imported + importProgress.errors, total: importProgress.total })}</p>
                </div>
                <div className="w-full bg-muted rounded-full h-2.5">
                    <div className="bg-[var(--vf-accent)] h-2.5 rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>
                <div className="grid grid-cols-3 gap-3 text-center text-sm">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                        <div className="font-bold text-green-700">{importProgress.imported}</div>
                        <div className="text-green-600 text-xs">{t('dialer:addProspect.progress.imported')}</div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                        <div className="font-bold text-amber-700">{importProgress.skipped}</div>
                        <div className="text-amber-600 text-xs">{t('dialer:addProspect.progress.skipped')}</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                        <div className="font-bold text-red-700">{importProgress.errors}</div>
                        <div className="text-red-600 text-xs">{t('dialer:addProspect.progress.errors')}</div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Step: Done ──
    if (step === 'done') {
        const { imported, skipped, errors, total, blacklisted } = importProgress;
        const success = imported > 0 && errors === 0;
        return (
            <div className="space-y-4 py-2">
                <StepIndicator current="done" />
                <div className={`rounded-lg border p-4 ${success ? 'bg-green-50 border-green-200' : errors > 0 ? 'bg-amber-50 border-amber-200' : 'bg-muted border-border'}`}>
                    <div className="flex items-center gap-2 mb-3">
                        {success ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : errors > 0 ? (
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                        ) : (
                            <Info className="w-5 h-5 text-muted-foreground" />
                        )}
                        <span className="font-semibold text-sm">
                            {success
                                ? t('dialer:addProspect.done.success')
                                : errors > 0
                                    ? t('dialer:addProspect.done.partial')
                                    : t('dialer:addProspect.done.none')}
                        </span>
                    </div>
                    <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">{t('dialer:addProspect.done.totalRows')}</span>
                            <span className="font-medium">{total}</span>
                        </div>
                        {imported > 0 && (
                            <div className="flex justify-between text-green-700">
                                <span>{t('dialer:addProspect.done.imported')}</span>
                                <span className="font-bold">{imported}</span>
                            </div>
                        )}
                        {skipped > 0 && (
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">{t('dialer:addProspect.done.skipped')}</span>
                                <span className="font-medium">{skipped}</span>
                            </div>
                        )}
                {errors > 0 && (
                            <div className="flex justify-between text-red-700">
                                <span>{t('dialer:addProspect.done.errors')}</span>
                                <span className="font-bold">{errors}</span>
                            </div>
                        )}
                        {blacklisted > 0 && (
                            <div className="flex justify-between text-sky-700">
                                <span className="flex items-center gap-1.5">
                                    <ShieldAlert className="w-3.5 h-3.5" />
                                    {t('dialer:addProspect.done.blacklisted')}
                                </span>
                                <span className="font-bold">{blacklisted}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Bloc blacklist : extrait des prospects auto-marqués 'ne pas appeler' */}
                {blacklistedSamples.length > 0 && (
                    <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-3">
                        <div className="flex items-center gap-2 mb-1">
                            <ShieldAlert className="w-4 h-4 text-sky-600" />
                            <span className="text-sm font-semibold text-sky-900">
                                {t('dialer:addProspect.done.blacklistTitle')}
                            </span>
                        </div>
                        <p className="text-[11px] text-sky-800 mb-2">
                            {t('dialer:addProspect.done.blacklistHint')} <strong>{t('dialer:addProspect.done.blacklistPath')}</strong>.
                        </p>
                        <div className="max-h-32 overflow-y-auto rounded border border-sky-200 bg-white divide-y divide-sky-100">
                            {blacklistedSamples.slice(0, 10).map((b, i) => (
                                <div key={i} className="px-2 py-1 text-[11px] grid grid-cols-[auto_1fr_auto] gap-2 items-center">
                                    <span className="font-mono text-muted-foreground">{t('dialer:addProspect.done.rowLabel', { row: b.rowNumber })}</span>
                                    <span className="truncate" title={b.sample}>{b.sample}</span>
                                    <span className="text-sky-700 font-mono">{b.phone}</span>
                                </div>
                            ))}
                        </div>
                        {blacklistedSamples.length > 10 && (
                            <p className="mt-1 text-[10px] text-muted-foreground italic">
                                {t('dialer:addProspect.done.blacklistMore', { n: blacklisted })}
                            </p>
                        )}
                    </div>
                )}

                {/* Détails des erreurs : top causes regroupées + liste détaillée */}
                {importErrors.length > 0 && (() => {
                    const grouped = importErrors.reduce((acc, e) => {
                        const human = humanizeImportReason(e.reason, t);
                        acc[human] = (acc[human] || 0) + 1;
                        return acc;
                    }, {});
                    const groupedSorted = Object.entries(grouped).sort((a, b) => b[1] - a[1]);
                    const totalCollected = importErrors.length;
                    const visibleErrors = showAllErrors ? importErrors : importErrors.slice(0, 5);
                    return (
                        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="w-4 h-4 text-amber-600" />
                                <span className="text-sm font-semibold text-amber-900">{t('dialer:addProspect.done.errorCauses')}</span>
                            </div>
                            <ul className="space-y-1 mb-3">
                                {groupedSorted.map(([reason, count]) => (
                                    <li key={reason} className="flex justify-between text-xs">
                                        <span className="text-amber-900">{reason}</span>
                                        <span className="font-medium text-amber-900 tabular-nums">{count}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="border-t border-amber-200 pt-2">
                                <div className="text-[11px] font-medium text-amber-900 mb-1">{t('dialer:addProspect.done.errorRows')}</div>
                                <div className="max-h-40 overflow-y-auto rounded border border-amber-200 bg-white divide-y divide-amber-100">
                                    {visibleErrors.map((e, i) => (
                                        <div key={i} className="px-2 py-1 text-[11px] grid grid-cols-[auto_1fr_1.2fr] gap-2 items-center">
                                            <span className="font-mono text-muted-foreground">{t('dialer:addProspect.done.rowLabel', { row: e.rowNumber })}</span>
                                            <span className="truncate" title={e.sample}>{e.sample}</span>
                                            <span className="text-amber-700 truncate" title={e.reason}>{humanizeImportReason(e.reason, t)}</span>
                                        </div>
                                    ))}
                                </div>
                                {totalCollected > 5 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAllErrors((v) => !v)}
                                        className="mt-1.5 text-[11px] text-amber-800 hover:underline"
                                    >
                                        {showAllErrors
                                            ? t('dialer:addProspect.done.collapse')
                                            : errors > totalCollected
                                                ? t('dialer:addProspect.done.seeAllOf', { n: totalCollected, total: errors })
                                                : t('dialer:addProspect.done.seeAll', { n: totalCollected })}
                                    </button>
                                )}
                                {errors > totalCollected && !showAllErrors && (
                                    <p className="mt-1 text-[10px] text-muted-foreground italic">
                                        {t('dialer:addProspect.done.limited', { n: totalCollected })}
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })()}

                <div className="flex gap-2">
                    {errors > 0 && headers.length > 0 && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                // Garde le fichier + mapping deja choisi, repart juste a
                                // l'etape mapping pour permettre a l'agent d'ajuster avant
                                // de relancer (ex. corriger une colonne mal mappee).
                                setStep('mapping');
                                setImportDone(false);
                                setImportProgress({ total: 0, imported: 0, skipped: 0, errors: 0, blacklisted: 0 });
                                setImportErrors([]);
                                setShowAllErrors(false);
                                setBlacklistedSamples([]);
                            }}
                            className="flex-1"
                        >
                            <ArrowLeft className="w-4 h-4 mr-1.5" />
                            {t('dialer:addProspect.done.backToMapping')}
                        </Button>
                    )}
                    <Button variant="outline" onClick={reset} className="flex-1">
                        {t('dialer:addProspect.done.importAnother')}
                    </Button>
                    <Button
                        onClick={() => { if (onDone) onDone(); }}
                        className="flex-1 bg-primary hover:bg-primary/90 text-white"
                    >
                        {t('common:actions.close')}
                    </Button>
                </div>
            </div>
        );
    }

    return null;
}

export default function AddProspectDialog({ clientEmail, defaultSource, onProspectAdded, trigger, existingSources, externalOpen, onExternalOpenChange }) {
    const { t } = useTranslation(['admin', 'common', 'dialer']);
    const { selectedCustomerId } = useDialerData();
    const { data: hubspotStatus } = useQuery({
        queryKey: ['hubspot-status', clientEmail || '_self'],
        queryFn: () =>
            api.functions.invoke('getHubSpotStatus', { clientEmail }).then((r) => r.data),
        // HubSpot hidden from the UI (AUDIT-INT-08, src/lib/hubspotUi.ts):
        // no status call, no HubSpot import tab.
        enabled: HUBSPOT_UI_ENABLED && !!clientEmail,
    });
    const hubspotConnected = HUBSPOT_UI_ENABLED && hubspotStatus?.connected === true;
    const [internalOpen, setInternalOpen] = useState(false);
    const open = externalOpen !== undefined ? externalOpen : internalOpen;
    const setOpen = externalOpen !== undefined ? (v) => onExternalOpenChange?.(v) : setInternalOpen;
    const { setSelectedProspect } = useDialerUI();
    const [loading, setLoading] = useState(false);

    // Manual form
    const [formData, setFormData] = useState({
        prospect_name: '',
        prospect_phone: '',
        // #96 -- second numero par defaut. Stocke dans `additional_phones` a
        // l'enregistrement : c'est le tableau deja expose par ProspectQueue et
        // deja lu par la recherche rapide (#84).
        prospect_mobile: '',
        prospect_email: '',
        prospect_company: '',
        prospect_title: '',
        linkedin_url: '',
        website: '',
        priority: 0,
        notes: ''
    });

    // HubSpot CRM
    const [hubspotLists, setHubspotLists] = useState([]);
    const [selectedList, setSelectedList] = useState('');
    const [loadingHubspot, setLoadingHubspot] = useState(false);
    const [hubspotListsLoaded, setHubspotListsLoaded] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const loadHubspotLists = async () => {
        if (hubspotListsLoaded || loadingHubspot) return;
        setLoadingHubspot(true);
        try {
            const result = await api.functions.invoke('getHubSpotLists', { clientEmail });
            const lists = Array.isArray(result.data) ? result.data : [];
            setHubspotLists(lists);
            setHubspotListsLoaded(true);
            if (lists.length === 0) toast.info(t('dialer:addProspect.toasts.hubspotNoList'));
        } catch (error) {
            toast.error(error.message || t('dialer:addProspect.toasts.hubspotError'));
        } finally {
            setLoadingHubspot(false);
        }
    };

    const handleManualSubmit = async (e) => {
        e.preventDefault();
        if (!formData.prospect_name || !formData.prospect_phone) {
            toast.error(t('dialer:addProspect.toasts.nameAndPhoneRequired'));
            return;
        }
        if (!selectedCustomerId) {
            toast.error(t('dialer:addProspect.toasts.selectClientFirst'));
            return;
        }

        setLoading(true);
        try {
            // Duplicate guard: a prospect's status is read from the last call to
            // its NUMBER, so a second row with an already-called number shows
            // that call's status and leaves "Ready to call" at once (a manual
            // create on 2026-09-27 reused a number with 66 calls on 4 rows).
            // Refuse it and name the existing rows instead.
            const digits = String(formData.prospect_phone).replace(/[^\d+]/g, '');
            const e164 = digits.startsWith('+') ? digits : digits.startsWith('0') ? '+33' + digits.slice(1) : '+' + digits;
            const existing = ((await api.entities.ProspectQueue.filter({ customer_id: selectedCustomerId, prospect_phone: e164 })) || [])
                .filter((p) => p.status !== 'placeholder');
            if (existing.length > 0) {
                const who = existing.slice(0, 3).map((p) => `${p.prospect_name || '?'} (${p.source || 'Manual'})`).join(', ');
                // Name the existing record AND open it: the search used to miss it.
                toast.error(t('dialer:addProspect.toasts.phoneExists', { who, count: existing.length }), {
                    action: {
                        label: t('dialer:addProspect.toasts.openExisting'),
                        onClick: () => {
                            setSelectedProspect(existing[0]);
                            setOpen(false);
                        },
                    },
                });
                setLoading(false);
                return;
            }
            // #96 -- `prospect_mobile` n'est pas un champ du schema : il alimente
            // `additional_phones`, la ou le second numero vit deja. On ne l'envoie
            // donc pas tel quel.
            const { prospect_mobile, ...champsSchema } = formData;
            await api.entities.ProspectQueue.create({
                customer_id: selectedCustomerId,
                ...champsSchema,
                ...(prospect_mobile && prospect_mobile.trim()
                    // i18n-exempt: phone label stored on the prospect record
                    ? { additional_phones: [{ number: prospect_mobile.trim(), label: 'Portable' }] }
                    : {}),
                source: defaultSource || 'Manual',
                status: 'active'
            });

            toast.success(t('admin:dialogs.addProspect.added'));
            setFormData({
                prospect_name: '',
                prospect_phone: '',
                prospect_mobile: '',
                prospect_email: '',
                prospect_company: '',
                prospect_title: '',
                linkedin_url: '',
                website: '',
                priority: 0,
                notes: ''
            });
            setOpen(false);
            if (onProspectAdded) onProspectAdded();
        } catch (error) {
            // QA-W7-2: translated by code (PERMISSION_DENIED names the
            // permission), never the server's English text; code under it.
            toast.error(t('dialer:addProspect.toasts.addError', { detail: errorMessage(error, t, 'common:errors.unknown') }), errorToastOptions(error));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setImportResult(null); }}>
            {trigger ? (
                <DialogTrigger asChild>
                    {trigger}
                </DialogTrigger>
            ) : (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <DialogTrigger asChild>
                                <Button className="bg-[var(--vf-accent-bg)] hover:bg-[var(--vf-accent-bd)] text-[var(--vf-accent-text)] border-0 h-9 w-9 p-0 ml-2 rounded-lg">
                                    <Plus className="w-5 h-5" />
                                </Button>
                            </DialogTrigger>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{t('dialer:addProspect.trigger')}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
            <DialogContent className="!max-w-[min(95vw,1100px)] !w-[min(95vw,1100px)] max-h-[90vh] p-0 flex flex-col overflow-hidden">
                <DialogHeader
                    className="px-6 pt-6 pb-4 border-b"
                    style={{ borderColor: 'var(--vf-border)' }}
                >
                    <div className="flex items-start gap-3">
                        <div
                            className="flex-shrink-0 flex items-center justify-center rounded-xl"
                            style={{
                                width: 40,
                                height: 40,
                                background: 'var(--vf-accent-bg)',
                                color: 'var(--vf-accent)',
                            }}
                        >
                            <UserPlus className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <DialogTitle
                                className="text-[18px] font-semibold leading-tight tracking-tight"
                                style={{
                                    color: 'var(--vf-text-1)',
                                    letterSpacing: '-0.01em',
                                }}
                            >
                                {t('admin:dialogs.addProspect.title')}
                            </DialogTitle>
                            <DialogDescription
                                className="text-[13px] mt-1 leading-relaxed"
                                style={{ color: 'var(--vf-text-3)' }}
                            >
                                {hubspotConnected
                                    ? t('dialer:addProspect.subtitleWithCrm')
                                    : t('dialer:addProspect.subtitle')}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="px-6 pb-6 pt-4 flex-1 overflow-y-auto min-h-0">
                <Tabs defaultValue="import" className="w-full" onValueChange={(v) => { if (v === 'crm') loadHubspotLists(); }}>
                    <TabsList
                        className={`grid w-full ${hubspotConnected ? 'grid-cols-3' : 'grid-cols-2'} h-auto p-1 mb-5`}
                        style={{ background: 'var(--vf-surface-2)' }}
                    >
                        <TabsTrigger
                            value="manual"
                            className="flex items-center gap-2 py-2 data-[state=active]:bg-card data-[state=active]:text-[var(--vf-accent)] data-[state=active]:shadow-sm text-[13px] font-medium"
                        >
                            <UserPlus className="w-3.5 h-3.5" />
                            {t('dialer:addProspect.tabs.manual')}
                        </TabsTrigger>
                        <TabsTrigger
                            value="import"
                            className="flex items-center gap-2 py-2 data-[state=active]:bg-card data-[state=active]:text-[var(--vf-accent)] data-[state=active]:shadow-sm text-[13px] font-medium"
                        >
                            <FileSpreadsheet className="w-3.5 h-3.5" />
                            {t('dialer:addProspect.tabs.file')}
                        </TabsTrigger>
                        {hubspotConnected && (
                            <TabsTrigger
                                value="crm"
                                className="flex items-center gap-2 py-2 data-[state=active]:bg-card data-[state=active]:text-[var(--vf-accent)] data-[state=active]:shadow-sm text-[13px] font-medium"
                            >
                                <Users className="w-3.5 h-3.5" />
                                {t('dialer:addProspect.tabs.hubspot')}
                            </TabsTrigger>
                        )}
                    </TabsList>

                    <TabsContent value="manual" className="space-y-4">
                        <form onSubmit={handleManualSubmit} className="space-y-4">
                            <div>
                                <Label>{t('common:field.name')} *</Label>
                                <Input
                                    value={formData.prospect_name}
                                    onChange={(e) => setFormData({ ...formData, prospect_name: e.target.value })}
                                    placeholder={t('dialer:addProspect.form.namePlaceholder')}
                                    required
                                />
                            </div>

                            <div>
                                <Label>{t('common:field.phone')} 1 *</Label>
                                <Input
                                    value={formData.prospect_phone}
                                    onChange={(e) => setFormData({ ...formData, prospect_phone: e.target.value })}
                                    placeholder="+33612345678"
                                    required
                                />
                            </div>


                            <div>
                                <Label>{t('common:field.email')}</Label>
                                <Input
                                    type="email"
                                    value={formData.prospect_email}
                                    onChange={(e) => setFormData({ ...formData, prospect_email: e.target.value })}
                                    placeholder={t('dialer:addProspect.form.emailPlaceholder')}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>{t('common:field.company')}</Label>
                                    <Input
                                        value={formData.prospect_company}
                                        onChange={(e) => setFormData({ ...formData, prospect_company: e.target.value })}
                                        placeholder={t('dialer:addProspect.form.companyPlaceholder')}
                                    />
                                </div>
                                <div>
                                    <Label>{t('common:field.title')}</Label>
                                    <Input
                                        value={formData.prospect_title}
                                        onChange={(e) => setFormData({ ...formData, prospect_title: e.target.value })}
                                        placeholder="CEO"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>{t('dialer:addProspect.form.linkedinUrl')}</Label>
                                    <Input
                                        value={formData.linkedin_url}
                                        onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                                        placeholder="https://linkedin.com/in/..."
                                    />
                                </div>
                                <div>
                                    <Label>{t('dialer:addProspect.form.website')}</Label>
                                    <Input
                                        value={formData.website}
                                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                        placeholder="https://example.com"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label>{t('dialer:addProspect.form.priority')}</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="10"
                                    value={formData.priority}
                                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                                />
                            </div>

                            <div>
                                <Label>{t('common:field.notes')}</Label>
                                <Textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    placeholder={t('dialer:addProspect.form.notesPlaceholder')}
                                    rows={3}
                                />
                            </div>

                            <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-white">
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {t('dialer:addProspect.form.submitting')}
                                    </>
                                ) : (
                                    t('dialer:addProspect.form.submit')
                                )}
                            </Button>
                        </form>
                    </TabsContent>

                    {hubspotConnected && (
                    <TabsContent value="crm" className="space-y-4">
                        <div className="space-y-4">
                            {importResult ? (
                                <div className="space-y-3">
                                    <div className={`rounded-lg border p-4 ${importResult.newProspects > 0 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                                        <div className="flex items-center gap-2 mb-3">
                                            {importResult.newProspects > 0 ? (
                                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                                            ) : (
                                                <Info className="w-5 h-5 text-amber-600" />
                                            )}
                                            <span className="font-semibold text-sm">
                                                {importResult.newProspects > 0
                                                    ? t('dialer:addProspect.hubspot.resultSuccess')
                                                    : t('dialer:addProspect.hubspot.resultNone')}
                                            </span>
                                        </div>
                                        <div className="space-y-1.5 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">{t('dialer:addProspect.hubspot.totalContacts')}</span>
                                                <span className="font-medium">{importResult.totalContacts}</span>
                                            </div>
                                            {importResult.newProspects > 0 && (
                                                <div className="flex justify-between text-green-700">
                                                    <span>{t('dialer:addProspect.hubspot.newProspects')}</span>
                                                    <span className="font-bold">{importResult.newProspects}</span>
                                                </div>
                                            )}
                                            {importResult.skippedDuplicates > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">{t('dialer:addProspect.hubspot.duplicates')}</span>
                                                    <span className="font-medium">{importResult.skippedDuplicates}</span>
                                                </div>
                                            )}
                                            {importResult.skippedNoPhone > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">{t('dialer:addProspect.hubspot.noPhone')}</span>
                                                    <span className="font-medium">{importResult.skippedNoPhone}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <Button
                                        onClick={() => {
                                            setImportResult(null);
                                            setOpen(false);
                                            if (onProspectAdded) onProspectAdded();
                                        }}
                                        className="w-full"
                                    >
                                        {t('common:actions.close')}
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <Label>{t('dialer:addProspect.hubspot.chooseList')}</Label>
                                        {loadingHubspot ? (
                                            <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                {t('dialer:addProspect.hubspot.loadingLists')}
                                            </div>
                                        ) : (
                                            <select
                                                value={selectedList}
                                                onChange={(e) => setSelectedList(e.target.value)}
                                                className="w-full border border-border rounded-lg p-2 mt-1"
                                                disabled={loadingHubspot}
                                            >
                                                <option value="">
                                                    {hubspotListsLoaded && hubspotLists.length === 0
                                                        ? t('dialer:addProspect.hubspot.noLists')
                                                        : t('dialer:addProspect.hubspot.selectList')}
                                                </option>
                                                {hubspotLists.map((list) => (
                                                    <option key={list.id} value={list.id}>
                                                        {list.name} {list.size > 0 ? t('dialer:addProspect.hubspot.listSize', { n: list.size }) : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>

                                    <Button
                                        onClick={async () => {
                                            if (!selectedList) {
                                                toast.error(t('dialer:addProspect.toasts.selectList'));
                                                return;
                                            }
                                            setImporting(true);
                                            setImportResult(null);
                                            try {
                                                const result = await api.functions.invoke('syncHubSpotContacts', { clientEmail, listId: selectedList });
                                                const data = result.data || {};
                                                setImportResult({
                                                    totalContacts: data.totalContacts ?? 0,
                                                    newProspects: data.newProspects ?? 0,
                                                    skippedDuplicates: data.skippedDuplicates ?? 0,
                                                    skippedNoPhone: data.skippedNoPhone ?? 0,
                                                });
                                                setSelectedList('');
                                            } catch (error) {
                                                toast.error(t('dialer:addProspect.toasts.hubspotErrorDetail', { detail: error.data?.error || error.message }));
                                            } finally {
                                                setImporting(false);
                                            }
                                        }}
                                        disabled={importing || !selectedList}
                                        className="w-full bg-primary hover:bg-primary/90 text-white"
                                    >
                                        {importing ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                {t('dialer:addProspect.hubspot.syncing')}
                                            </>
                                        ) : (
                                            t('dialer:addProspect.hubspot.sync')
                                        )}
                                    </Button>
                                </>
                            )}
                        </div>
                    </TabsContent>
                    )}

                    <TabsContent value="import" className="space-y-4">
                        <FileImportFlow
                            clientEmail={clientEmail}
                            existingSources={existingSources}
                            onDone={() => {
                                setOpen(false);
                                if (onProspectAdded) onProspectAdded();
                            }}
                        />
                    </TabsContent>
                </Tabs>
                </div>
            </DialogContent>
        </Dialog>
    );
}
